/**
 * useNewsletters — newsletter series + recent issues for the My Marketing hub,
 * with realtime refresh while issues are sending.
 *
 * Data comes from the newsletters DAL (anon Supabase client, app-enforced
 * owner_id scoping). Privileged actions (send now / test send) go through the
 * us-east1 callables.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { supabase, isSupabaseConfigured } from '../utils/supabase';
import { functionsUsEast1 } from '../utils/init-firebase';
import { fetchNewsletters, fetchRecentIssues } from '../api/supabase/newsletters';

const REALTIME_DEBOUNCE_MS = 800;

export function useNewsletters(ownerId) {
  const [newsletters, setNewsletters] = useState([]);
  const [recentIssues, setRecentIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!ownerId || !isSupabaseConfigured()) {
      setNewsletters([]);
      setRecentIssues([]);
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const [newsletterRows, issueRows] = await Promise.all([
        fetchNewsletters(ownerId),
        fetchRecentIssues(ownerId, { limit: 50 }),
      ]);
      setNewsletters(newsletterRows);
      setRecentIssues(issueRows);
    } catch (err) {
      console.error('[useNewsletters] Load failed:', err);
      setError(err.message || 'Failed to load newsletters');
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Realtime: refresh on newsletter edits and on issue progress (counters
  // update as processEmailQueue drains each send).
  useEffect(() => {
    if (!ownerId || !isSupabaseConfigured()) return undefined;
    let debounceTimer = null;
    const scheduleRefresh = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(load, REALTIME_DEBOUNCE_MS);
    };
    const channel = supabase
      .channel(`my-marketing-newsletters-${ownerId}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'crm_newsletters', filter: `owner_id=eq.${ownerId}` },
        scheduleRefresh
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'crm_newsletter_issues', filter: `owner_id=eq.${ownerId}` },
        scheduleRefresh
      )
      .subscribe();
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [ownerId, load]);

  // recentIssues arrives sorted newest-first, so first hit per newsletter wins.
  const latestIssueByNewsletter = useMemo(() => {
    const map = {};
    for (const issue of recentIssues) {
      if (!map[issue.newsletterId]) map[issue.newsletterId] = issue;
    }
    return map;
  }, [recentIssues]);

  const sendNewsletterNow = useCallback(async (newsletterId) => {
    const fn = httpsCallable(functionsUsEast1, 'sendNewsletterNow');
    const res = await fn({ newsletterId });
    return res.data;
  }, []);

  /**
   * @param {Object} payload - { newsletterId } for saved newsletters, or
   *   { subject, bodyHtml, audienceConfig } for unsaved editor content.
   */
  const sendTestNewsletter = useCallback(async (payload) => {
    const fn = httpsCallable(functionsUsEast1, 'testSendNewsletter');
    const res = await fn(payload);
    return res.data;
  }, []);

  return {
    newsletters,
    recentIssues,
    latestIssueByNewsletter,
    loading,
    error,
    refresh: load,
    sendNewsletterNow,
    sendTestNewsletter,
  };
}

export default useNewsletters;
